import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useMemo, useState } from "react";
import { FaDownload, FaFileExcel } from "react-icons/fa";
import { NegotiationRoundDto, ReceivedBseBidDto, useGetReceivedBseBidQuery } from "../../../../../autogen/bff-api";
import {
  downloadExcel,
  getProductGroupPrice,
  mapBidToProductGroups,
} from "../../../../../common/products/productUtils";
import { Product, ProductField, ProductGroup } from "../../../../../common/types";
import { useNegotiation } from "../NegotiationContext";
import { ProductRow } from "./ProductRow";

type ComparisonTarget = { kind: "original" } | { kind: "round"; roundId: string; roundNumber: number };

export const PriceChangesTab = ({
  eventId,
  organizationId,
  rounds,
  originalEventBidId,
  productGroups,
  selectedBid,
}: {
  eventId: string;
  organizationId: string;
  rounds: NegotiationRoundDto[];
  originalEventBidId?: string;
  productGroups: ProductGroup[];
  selectedBid?: ReceivedBseBidDto;
}) => {
  const { roundNumber } = useNegotiation();
  const [comparisonTarget, setComparisonTarget] = useState<ComparisonTarget>({ kind: "original" });

  const comparisonRoundBidId =
    comparisonTarget.kind === "round"
      ? rounds.find((r) => r.id === comparisonTarget.roundId)?.invites?.find((i) => i.organizationId === organizationId)
          ?.bidId
      : undefined;

  const comparisonBidId =
    roundNumber > 0 ? (comparisonTarget.kind === "round" ? comparisonRoundBidId : originalEventBidId) : undefined;

  const { data: bidForComparison } = useGetReceivedBseBidQuery(
    { eventId, bidId: comparisonBidId ?? "" },
    { skip: !comparisonBidId }
  );

  const selectedGroups = useMemo(() => mapBidToProductGroups(productGroups, selectedBid), [productGroups, selectedBid]);

  const comparisonGroups = mapBidToProductGroups(productGroups, bidForComparison);

  return (
    <Flex direction="column" gap={3} width="full" overflowX="hidden">
      <Flex gap={3} align="center" justify={roundNumber === 0 ? "flex-end" : "space-between"} wrap="wrap">
        {roundNumber > 0 && (
          <Flex gap={3} align="center" wrap="wrap">
            <Text>{t("Compare the bid from the selected round to")}</Text>
            <Menu isLazy placement="bottom-start">
              <MenuButton as={Button} type="button" size="sm" variant="outline" rightIcon={<ChevronDownIcon />}>
                {comparisonTarget.kind === "round"
                  ? `${t("Round")} ${comparisonTarget.roundNumber}`
                  : t("Original event")}
              </MenuButton>
              <MenuList zIndex={2000}>
                <MenuItem onClick={() => setComparisonTarget({ kind: "original" })}>{t("Original event")}</MenuItem>
                {rounds
                  .filter((r) => r.roundNumber < roundNumber)
                  .map((r) => (
                    <MenuItem
                      key={r.id}
                      onClick={() => setComparisonTarget({ kind: "round", roundId: r.id, roundNumber: r.roundNumber })}
                    >
                      {t("Negotiation round")} {r.roundNumber}
                    </MenuItem>
                  ))}
              </MenuList>
            </Menu>
          </Flex>
        )}
        <Button
          size="sm"
          variant="ghost"
          leftIcon={<Icon as={FaFileExcel} />}
          rightIcon={<Icon as={FaDownload} />}
          onClick={() =>
            downloadExcel({
              productGroups: selectedGroups ?? [],
              fileName: `bid_${selectedBid?.owningOrganization.name}`,
            })
          }
        >
          {t("Download products")}
        </Button>
      </Flex>
      {selectedGroups ? (
        <Box overflowX="auto" width="full" maxWidth="full">
          {selectedGroups.map((group) => {
            const groupForComparison = comparisonGroups?.find((g) => g.id === group.id);
            const selectedTotal = getProductGroupPrice(group);
            const totalForComparison = groupForComparison ? getProductGroupPrice(groupForComparison) : undefined;
            const deltaTotal =
              selectedTotal != null && totalForComparison != null ? selectedTotal - totalForComparison : undefined;
            const deltaTotalPercentage =
              deltaTotal != null && !!totalForComparison ? (deltaTotal / totalForComparison) * 100 : undefined;
            return (
              <Box
                key={group.id}
                width="full"
                border="1px solid"
                borderColor="smBorder"
                rounded="lg"
                pt="4"
                pb="2.5"
                my="2"
              >
                <Text as="h2" fontWeight={"semibold"} px="4">
                  {group.name}
                </Text>
                <Table variant="simple" size="sm" width="full">
                  <Thead>
                    <Tr>
                      {group.productFields
                        .filter((field: ProductField) => field.populatedBy === "Buyer")
                        .map((field: ProductField) => (
                          <Th key={field.id}>{field.name}</Th>
                        ))}
                      <Th>{t("Quantity")}</Th>
                      <Th>{roundNumber === 0 ? t("Price") : t("Price (Round {{n}})", { n: roundNumber })}</Th>
                      {roundNumber > 0 && (
                        <>
                          <Th>
                            {comparisonTarget.kind === "round"
                              ? t("Price (Round {{n}})", { n: comparisonTarget.roundNumber })
                              : t("Price (Original event)")}
                          </Th>
                          <Th>{t("Change_noun")}</Th>
                          <Th>{t("Change_noun %")}</Th>
                        </>
                      )}
                      {group.productFields
                        .filter((field: ProductField) => field.populatedBy === "Supplier")
                        .map((field: ProductField) => (
                          <Th key={field.id}>{field.name}</Th>
                        ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {group.products?.map((product: Product) => (
                      <ProductRow
                        key={product.id}
                        product={product}
                        productForComparison={
                          roundNumber > 0 ? groupForComparison?.products?.find((p) => p.id === product.id) : undefined
                        }
                        showComparison={roundNumber > 0}
                      />
                    ))}
                    <Tr fontWeight={"bold"}>
                      <Td>{t("Group total")}</Td>
                      {group.productFields
                        .filter((field: ProductField) => field.populatedBy === "Buyer")
                        .map((field: ProductField) => (
                          <Td key={field.id}></Td>
                        ))}
                      <Td verticalAlign="top">
                        <Text fontWeight="semibold" color="smPrimary">
                          {selectedTotal != null ? selectedTotal.toLocaleString() : "—"}
                        </Text>
                      </Td>
                      {roundNumber > 0 && (
                        <>
                          <Td verticalAlign="top">
                            <Text fontWeight="semibold" color="smSecondary">
                              {totalForComparison != null ? totalForComparison.toLocaleString() : "—"}
                            </Text>
                          </Td>
                          <Td
                            color={
                              deltaTotal == null
                                ? "smMuted"
                                : deltaTotal < 0
                                  ? "green.500"
                                  : deltaTotal > 0
                                    ? "red.500"
                                    : "smMuted"
                            }
                            verticalAlign="top"
                          >
                            <Text fontWeight="semibold">
                              {deltaTotal != null ? (deltaTotal === 0 ? "0" : deltaTotal.toLocaleString()) : "—"}
                            </Text>
                          </Td>
                          <Td
                            color={
                              deltaTotalPercentage == null
                                ? "smMuted"
                                : deltaTotalPercentage < 0
                                  ? "green.500"
                                  : deltaTotalPercentage > 0
                                    ? "red.500"
                                    : "smMuted"
                            }
                            verticalAlign="top"
                          >
                            <Text fontWeight="semibold">
                              {deltaTotalPercentage != null
                                ? `${(Math.round(deltaTotalPercentage * 10) / 10).toLocaleString()}%`
                                : "—"}
                            </Text>
                          </Td>
                        </>
                      )}
                      {group.productFields
                        .filter((field: ProductField) => field.populatedBy === "Supplier")
                        .map((field: ProductField) => (
                          <Td key={field.id}></Td>
                        ))}
                    </Tr>
                  </Tbody>
                </Table>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Text color="smMuted">
          {roundNumber === 0 ? t("No bid delivered for the original event") : t("No bid delivered for this selection")}
        </Text>
      )}
    </Flex>
  );
};
